





































































































































































































import {Component, Prop, Vue} from 'vue-property-decorator'
import {BridgeNetworks} from '@/model/bridge/types/BridgeNetworks'
import {BridgeNetworkHelper} from '@/helpers/BridgeNetworkHelper'
import {TBridgeLegStatus} from '@/helpers/BridgeTrackingHelper'
import {$} from '@/facade'

type TTrackingLeg = {
  key: 'source' | 'target'
  status: TBridgeLegStatus
  label: string
  pendingHint: string
  iconClass: string
  transactionHash: string | null
  explorerUrl: string
  explorerText: string
  explorerIcon: string
  explorerIconClass: string
  explorerAlt: string
}

@Component
export default class BridgeTrackingStep extends Vue {
  @Prop({type: String, required: true}) sourceNetwork!: BridgeNetworks
  @Prop({type: String, required: true}) sourceStatus!: TBridgeLegStatus
  @Prop({type: String, default: null}) sourceTransactionHash!: string | null
  @Prop({type: String, required: true}) targetStatus!: TBridgeLegStatus
  @Prop({type: String, default: null}) targetTransactionHash!: string | null

  get targetNetwork(): BridgeNetworks {
    return this.sourceNetwork === 'N3' ? 'X' : 'N3'
  }

  get isRunning() {
    return (
      this.sourceStatus === 'pending' ||
      this.sourceStatus === 'idle' ||
      this.targetStatus === 'pending'
    )
  }

  get isBridged() {
    return this.sourceStatus === 'success' && this.targetStatus === 'success'
  }

  get hasFailed() {
    return this.sourceStatus === 'error' || this.targetStatus === 'error'
  }

  get hasTimedOut() {
    return this.sourceStatus === 'timeout' || this.targetStatus === 'timeout'
  }

  get headerTitle() {
    if (this.isRunning) {
      return this.translate('inProgressTitle')
    }

    if (this.isBridged) {
      return this.translate('successTitle')
    }

    if (this.hasFailed) {
      return this.translate('failedTitle')
    }

    return this.translate('timeoutTitle')
  }

  get headerDescription() {
    if (this.isRunning) {
      return this.translate('inProgressDescription')
    }

    if (this.isBridged) {
      return this.translate('successDescription', {
        network: this.networkName(this.targetNetwork),
      })
    }

    if (this.hasFailed) {
      return this.translate('failedDescription')
    }

    return this.translate('timeoutDescription')
  }

  get headerTitleClass() {
    if (this.isBridged) {
      return 'text-primary'
    }

    if (this.hasFailed) {
      return 'text-coral-red'
    }

    if (this.hasTimedOut) {
      return 'text-reddish-orange'
    }

    return 'text-raspberry-pink'
  }

  get headerImageSrc() {
    if (this.isRunning) {
      return require('@/assets/img/SwapTokenLoading.svg')
    }

    if (this.isBridged) {
      return require('@/assets/img/SwapTokenSuccess.svg')
    }

    return require('@/assets/img/SwapTokenError.svg')
  }

  get headerImageAlt() {
    if (this.isRunning) {
      return 'loading'
    }

    return this.isBridged ? 'success' : 'error'
  }

  get legs(): TTrackingLeg[] {
    const legs: TTrackingLeg[] = [
      {
        key: 'source',
        status: this.sourceStatus,
        label: this.sourceLabel,
        pendingHint: this.translate('awaitingTransaction'),
        iconClass: this.iconClassOf(this.sourceStatus),
        transactionHash: this.sourceTransactionHash,
        ...this.explorerOf(this.sourceNetwork, this.sourceTransactionHash),
      },
    ]

    if (this.targetStatus === 'idle' && this.sourceStatus !== 'success') {
      return legs
    }

    return [
      ...legs,
      {
        key: 'target',
        status: this.targetStatus,
        label: this.targetLabel,
        pendingHint: this.translate('awaitingBridge'),
        iconClass: this.iconClassOf(this.targetStatus),
        transactionHash: this.targetTransactionHash,
        ...this.explorerOf(this.targetNetwork, this.targetTransactionHash),
      },
    ]
  }

  private get sourceLabel() {
    const isDeposit = this.sourceNetwork === 'N3'
    const labels: Record<TBridgeLegStatus, string> = {
      idle: isDeposit ? 'depositing' : 'withdrawing',
      pending: isDeposit ? 'depositing' : 'withdrawing',
      success: isDeposit ? 'deposited' : 'withdrawn',
      error: isDeposit ? 'depositFailed' : 'withdrawFailed',
      timeout: isDeposit ? 'depositUnconfirmed' : 'withdrawUnconfirmed',
    }

    return this.translate(labels[this.sourceStatus], {
      network: this.networkName(this.sourceNetwork),
    })
  }

  private get targetLabel() {
    const labels: Record<TBridgeLegStatus, string> = {
      idle: 'transferQueued',
      pending: 'transferring',
      success: 'transferred',
      error: 'transferFailed',
      timeout: 'transferUnconfirmed',
    }

    return this.translate(labels[this.targetStatus], {
      network: this.networkName(this.targetNetwork),
    })
  }

  private iconClassOf(status: TBridgeLegStatus) {
    switch (status) {
      case 'success':
        return 'text-primary'
      case 'error':
        return 'text-coral-red'
      case 'timeout':
        return 'text-reddish-orange'
      case 'pending':
        return 'text-raspberry-pink'
      default:
        return 'text-cadet-blue'
    }
  }

  private explorerOf(network: BridgeNetworks, transactionHash: string | null) {
    if (network === 'N3') {
      return {
        explorerUrl: transactionHash
          ? `${BridgeNetworkHelper.n3ExplorerUrl()}transactionInfo/${transactionHash}`
          : '',
        explorerText: $.translate(
          'components.BridgeTrackingStep.oneGateLinkText'
        ) as string,
        explorerIcon: require('@/assets/img/one-gate.png'),
        explorerIconClass: 'bg-bunker rounded-full p-1',
        explorerAlt: 'OneGate Icon',
      }
    }

    return {
      explorerUrl: transactionHash
        ? `${BridgeNetworkHelper.xExplorerUrl()}tx/${transactionHash}`
        : '',
      explorerText: $.translate(
        'components.BridgeTrackingStep.xExplorerLinkText'
      ) as string,
      explorerIcon: require('@/assets/img/neox.svg'),
      explorerIconClass: '',
      explorerAlt: 'X Explorer Icon',
    }
  }

  private networkName(network: BridgeNetworks) {
    return network === 'N3' ? 'Neo N3' : 'Neo X'
  }

  private translate(key: string, params?: Record<string, unknown>) {
    return $.translate(`components.BridgeTrackingStep.${key}`, params) as string
  }
}
